(() => {
  if (customElements.get('nh-navigation')) return;
  class NHNavigation extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const on = (target, event, handler, options = {}) => target.addEventListener(event, handler, { ...options, signal: this.abort.signal });
      this.input = this.querySelector('[data-search-input]');
      this.panel = this.querySelector('.nh-search__results');
      this.results = this.querySelector('[data-search-results]');
      this.discovery = this.querySelector('[data-search-discovery]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.status = this.querySelector('[role="status"]');
      this.menuButton = this.querySelector('[data-menu-toggle]');
      this.searchButton = this.querySelector('[data-search-toggle]');
      this.backdrop = this.querySelector('.nh-nav-backdrop');
      this.menu = this.querySelector('.nh-main-nav');
      this.mobile = matchMedia('(max-width: 1100px)');
      this.hoverPointer = matchMedia('(hover: hover) and (pointer: fine)');
      on(this.menuButton, 'click', () => {
        if (this.classList.contains('is-menu-open')) this.closeAll();
        else this.toggleMenu(true);
      });
      on(this.searchButton, 'click', () => {
        const wasSearchOpen = this.classList.contains('is-search-open');
        if (wasSearchOpen) this.closeAll();
        else this.openSearch();
      });
      on(this.backdrop, 'click', () => this.closeAll());
      on(document, 'click', event => { if (!this.contains(event.target)) this.closeAll(false); });
      on(document, 'keydown', event => this.handleOutsideEscape(event));
      on(document, 'focusin', event => {
        if (this.contains(event.target)) return;
        if (this.modalActive) this.focusableItems()[0]?.focus();
        else this.closeAll(false);
      });
      this.querySelectorAll('.nh-nav-item').forEach(details => {
        const enter = event => this.queueHoverOpen(details, event);
        const leave = event => this.queueHoverClose(event);
        on(details, 'pointerenter', enter);
        on(details, 'pointerleave', leave);
        // The absolutely positioned panel can be separated from its summary by
        // a small gap. Re-entry cancels the grace-period close without refocusing.
        const mega = details.querySelector('.nh-mega');
        if (mega) { on(mega, 'pointerenter', enter); on(mega, 'pointerleave', leave); }
        on(details.querySelector('summary'), 'pointerenter', enter);
        on(details.querySelector('summary'), 'pointerdown', () => this.clearHoverTimers());
        on(details, 'toggle', () => {
          if (details.open) {
            this.querySelectorAll('.nh-nav-item').forEach(other => { if (other !== details) other.open = false; });
            this.closeSearch();
          }
          this.updateBackdrop();
        });
      });
      on(this, 'keydown', event => {
        if (event.key === 'Escape') {
          event.preventDefault();
          const summary = !this.modalActive && event.target.closest('.nh-nav-item')?.querySelector('summary');
          this.closeAll();
          if (summary) summary.focus();
        }
        if (event.key === 'Tab' && this.modalActive) {
          this.trapFocus(event);
        }
        if (event.key === 'ArrowDown' && event.target === this.input && !this.panel.hidden) {
          const firstResult = this.results.querySelector('a') || this.discovery?.querySelector('a');
          if (firstResult) { event.preventDefault(); firstResult.focus(); }
        }
      });
      on(this.input, 'input', () => {
        clearTimeout(this.timer);
        this.searchRequest?.abort();
        this.searchVersion = (this.searchVersion || 0) + 1;
        if (this.clearButton) this.clearButton.hidden = !this.input.value;
        if (!this.classList.contains('is-search-open')) { this.openSearch(false); return; }
        if (this.input.value.trim().length < 2) { this.showDiscovery(); return; }
        this.timer = setTimeout(() => this.search(), 220);
      });
      on(this.input, 'focus', () => { if (!this.restoringSearchFocus && !this.openingSearch) this.openSearch(false); });
      on(this.input, 'click', () => { if (this.panel.hidden) this.openSearch(false); });
      const closeSearchButton = this.querySelector('[data-search-close]');
      if (closeSearchButton) on(closeSearchButton, 'click', () => this.closeAll(true));
      if (this.clearButton) on(this.clearButton, 'click', () => { this.input.value = ''; this.showDiscovery(); this.restoreSearchFocus(this.input); });
      this.querySelectorAll('[data-search-term]').forEach(link => {
        on(link, 'click', event => {
          if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
          event.preventDefault();
          clearTimeout(this.timer);
          this.input.value = link.dataset.searchTerm;
          this.openSearch();
        });
      });
      on(this.input.form, 'submit', () => this.closeSearch(false));
      // Both mobile entry points use the same product-only predictive search.
      const menuInput = this.querySelector('.nh-mobile-menu-search input[name="q"]');
      if (menuInput) {
        const openSharedSearch = () => {
          this.input.value = menuInput.value;
          this.openSearch();
        };
        on(menuInput, 'focus', openSharedSearch);
        on(menuInput, 'input', openSharedSearch);
      }
      this.querySelectorAll('[data-series-toggle]').forEach(button => {
        on(button, 'click', () => {
          if (!this.mobile.matches) return;
          const list = this.querySelector(`#${button.getAttribute('aria-controls')}`);
          if (!list) return;
          const expanded = button.getAttribute('aria-expanded') !== 'true';
          button.setAttribute('aria-expanded', String(expanded));
          list.hidden = !expanded;
        });
      });
      this.syncSeries();
      on(this.mobile, 'change', () => { this.closeAll(true); this.syncSeries(); });
      on(this.hoverPointer, 'change', () => this.clearHoverTimers());
      on(window, 'scroll', () => {
        if (this.scrollFrame) return;
        this.scrollFrame = requestAnimationFrame(() => {
          this.scrollFrame = null;
          this.classList.toggle('is-compact', window.scrollY > 100 && !this.classList.contains('is-menu-open'));
          this.updatePosition();
        });
      }, { passive: true });
      on(window, 'resize', () => { this.clearHoverTimers(); this.updatePosition(); }, { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.updatePosition());
      this.resizeObserver.observe(this);
      this.updatePosition();
      ['cart:updated', 'cart:refresh', 'nh:cart-updated'].forEach(event => on(document, event, () => this.refreshCart()));
      on(window, 'cartcount:update', () => this.refreshCart());
      on(window, 'pageshow', () => this.refreshCart());
      this.classList.add('is-enhanced');
      this.menuButton.hidden = false;
      this.searchButton.hidden = false;
      this.setAttribute('data-navigation-ready', '');
      window.nhNavigationSettled?.();
      this.updatePosition();
    }
    disconnectedCallback() {
      this.clearHoverTimers();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = null;
      this.abort?.abort(); this.searchRequest?.abort(); this.cartRequest?.abort();
      clearTimeout(this.timer); this.resizeObserver?.disconnect(); this.releaseModal();
    }
    updatePosition() { this.style.setProperty('--nh-header-bottom', `${Math.max(0, this.getBoundingClientRect().bottom)}px`); }
    syncSeries() {
      this.querySelectorAll('[data-series-toggle]').forEach(button => {
        const list = this.querySelector(`#${button.getAttribute('aria-controls')}`);
        if (!list) return;
        const heading = button.closest('.nh-mega__column').querySelector('[data-series-heading]');
        button.hidden = !this.mobile.matches;
        if (heading) heading.hidden = this.mobile.matches;
        list.hidden = this.mobile.matches;
        button.setAttribute('aria-expanded', String(!this.mobile.matches));
      });
    }
    clearHoverTimers() {
      if (this.hoverOpenTimer != null) clearTimeout(this.hoverOpenTimer);
      if (this.hoverCloseTimer != null) clearTimeout(this.hoverCloseTimer);
      this.hoverOpenTimer = null; this.hoverCloseTimer = null;
    }
    handleOutsideEscape(event) {
      if (event.key !== 'Escape' || this.contains(event.target)) return;
      if (this.hoverOpenTimer != null || this.hoverCloseTimer != null || this.querySelector('.nh-nav-item[open]')) this.closeAll(false);
    }
    canHover(event) { return !this.mobile.matches && this.hoverPointer.matches && event.pointerType !== 'touch'; }
    queueHoverOpen(details, event) {
      if (!this.canHover(event)) return;
      this.clearHoverTimers();
      if (details.open) return;
      this.hoverOpenTimer = setTimeout(() => {
        this.hoverOpenTimer = null;
        if (!this.canHover(event) || !this.isConnected) return;
        // Do not hide a keyboard user's focused link in a different panel.
        const focusedPanel = document.activeElement?.closest('.nh-nav-item[open]');
        if (focusedPanel && focusedPanel !== details && document.activeElement !== focusedPanel.querySelector('summary')) return;
        this.querySelectorAll('.nh-nav-item[open]').forEach(other => { if (other !== details) other.open = false; });
        details.open = true;
        this.updateBackdrop();
      }, 150);
    }
    queueHoverClose(event) {
      if (!this.canHover(event)) return;
      this.clearHoverTimers();
      this.hoverCloseTimer = setTimeout(() => {
        this.hoverCloseTimer = null;
        if (!this.canHover(event) || !this.isConnected) return;
        this.querySelectorAll('.nh-nav-item[open]').forEach(details => {
          if (!details.contains(document.activeElement)) details.open = false;
        });
        this.updateBackdrop();
      }, 250);
    }
    toggleMenu(open) {
      this.clearHoverTimers();
      this.closeSearch();
      this.classList.remove('is-search-open');
      this.searchButton.setAttribute('aria-expanded', 'false');
      this.searchButton.setAttribute('aria-label', 'Åbn søgning');
      this.classList.toggle('is-menu-open', open);
      this.menuButton.setAttribute('aria-expanded', String(open));
      this.menuButton.setAttribute('aria-label', open ? 'Luk menu' : 'Åbn menu');
      this.querySelector('[data-menu-icon]').hidden = open;
      this.querySelector('[data-close-icon]').hidden = !open;
      if (open) {
        const menuInput = this.querySelector('.nh-mobile-menu-search input[name="q"]');
        if (menuInput) menuInput.value = this.input.value;
        this.syncModal('Hovedmenu');
        this.updatePosition();
        this.menu.querySelector('summary, a[href]')?.focus();
      } else this.releaseModal();
      this.updateBackdrop();
    }
    openSearch(focus = true) {
      this.openingSearch = true;
      this.toggleMenu(false);
      this.querySelectorAll('.nh-nav-item[open]').forEach(details => { details.open = false; });
      this.classList.add('is-search-open');
      this.searchButton.setAttribute('aria-expanded', 'true');
      this.searchButton.setAttribute('aria-label', 'Luk søgning');
      this.syncModal('Søg produkter');
      if (focus) this.input.focus();
      this.openingSearch = false;
      if (this.input.value.trim().length >= 2) this.search();
      else this.showDiscovery();
      this.updateBackdrop();
    }
    restoreSearchFocus(target) {
      this.restoringSearchFocus = true;
      target.focus();
      this.restoringSearchFocus = false;
    }
    showDiscovery() {
      clearTimeout(this.timer); this.searchRequest?.abort();
      this.searchVersion = (this.searchVersion || 0) + 1;
      this.panel.hidden = false; this.panel.removeAttribute('aria-busy');
      this.panel.removeAttribute('data-search-active');
      if (this.discovery) this.discovery.hidden = false;
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
      this.results.replaceChildren(); this.status.textContent = '';
      this.updateBackdrop();
    }
    focusableItems() {
      return [...this.querySelectorAll('a[href],button,input,select,textarea,summary,[tabindex]')].filter(el => {
        if (el.tabIndex < 0 || el.disabled || !el.getClientRects().length || el.closest('[hidden],[inert]')) return false;
        const closed = el.closest('details:not([open])');
        return !closed || el === closed.querySelector('summary');
      });
    }
    trapFocus(event) {
      const items = this.focusableItems();
      const first = items[0]; const last = items[items.length - 1];
      if (!first) { event.preventDefault(); return; }
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    syncModal(label) {
      if (!this.mobile.matches) return;
      if (this.modalActive) { this.setAttribute('aria-label', label); return; }
      this.modalActive = true;
      this.setAttribute('role', 'dialog');
      this.setAttribute('aria-modal', 'true');
      this.setAttribute('aria-label', label);
      if (!this.scrollLocked) {
        this.previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.scrollLocked = true;
      }
      this.inertElements = new Map();
      const isolate = () => {
        for (let branch = this; branch.parentElement; branch = branch.parentElement) {
          for (const sibling of branch.parentElement.children) {
            if (sibling === branch || this.inertElements.has(sibling)) continue;
            this.inertElements.set(sibling, sibling.inert);
            sibling.inert = true;
          }
          if (branch.parentElement === document.body) break;
        }
      };
      isolate();
      this.inertObserver = new MutationObserver(isolate);
      this.inertObserver.observe(document.body, { childList: true, subtree: true });
    }
    releaseModal() {
      this.inertObserver?.disconnect();
      this.inertElements?.forEach((previous, element) => { element.inert = previous; });
      this.inertElements?.clear();
      this.modalActive = false;
      this.removeAttribute('role'); this.removeAttribute('aria-modal'); this.removeAttribute('aria-label');
      this.unlockScroll();
    }
    unlockScroll() { if (this.scrollLocked) { document.body.style.overflow = this.previousOverflow; this.scrollLocked = false; } }
    closeSearch(clear = true) {
      clearTimeout(this.timer); this.searchRequest?.abort();
      // a closed search starts empty next time (not on submit, where the term must still be sent)
      if (clear && this.input && this.panel && !this.panel.hidden) { this.input.value = ''; this.showDiscovery?.(); }
      this.searchVersion = (this.searchVersion || 0) + 1;
      this.panel.hidden = true;
      this.panel.removeAttribute('aria-busy');
      const wasSearch = this.classList.contains('is-search-open');
      this.classList.remove('is-search-open');
      this.searchButton.setAttribute('aria-expanded', 'false');
      this.searchButton.setAttribute('aria-label', 'Åbn søgning');
      if (wasSearch && !this.classList.contains('is-menu-open')) this.releaseModal();
      this.updateBackdrop();
    }
    closeAll(focus = true) {
      this.clearHoverTimers();
      const wasMenu = this.classList.contains('is-menu-open');
      const wasSearch = this.classList.contains('is-search-open');
      this.toggleMenu(false);
      this.querySelectorAll('.nh-nav-item[open]').forEach(details => { details.open = false; });
      this.updateBackdrop();
      if (focus && wasMenu) (this.menuButton.getClientRects().length ? this.menuButton : this.querySelector('.nh-header__logo')).focus();
      else if (focus && wasSearch) this.restoreSearchFocus(this.searchButton.getClientRects().length ? this.searchButton : this.input);
      else if (focus && this.contains(document.activeElement) && this.panel.contains(document.activeElement)) this.restoreSearchFocus(this.input);
    }
    updateBackdrop() {
      if (!this.backdrop) return;
      this.backdrop.hidden = !(this.querySelector('.nh-nav-item[open]') || !this.panel.hidden || this.classList.contains('is-search-open'));
    }
    async search() {
      this.clearHoverTimers();
      const query = this.input.value.trim();
      if (query.length < 2) { this.showDiscovery(); return; }
      this.searchRequest?.abort();
      const controller = new AbortController(); this.searchRequest = controller;
      const version = this.searchVersion = (this.searchVersion || 0) + 1;
      this.querySelectorAll('.nh-nav-item[open]').forEach(details => { details.open = false; });
      this.panel.hidden = false; this.panel.setAttribute('aria-busy', 'true');
      this.panel.setAttribute('data-search-active', '');
      if (this.discovery) this.discovery.hidden = true;
      if (this.clearButton) this.clearButton.hidden = false;
      this.status.textContent = 'Søger…'; this.results.replaceChildren(); this.updateBackdrop();
      const url = new URL(this.dataset.searchUrl, location.origin);
      url.search = new URLSearchParams({ q: query, 'resources[type]': 'product,article', 'resources[limit]': '10', 'resources[options][unavailable_products]': 'hide', section_id: 'nh-predictive-search' });
      try {
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const documentResult = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = documentResult.querySelector('[data-nh-predictive-results]');
        if (!content) throw new Error('Missing search results');
        if (version !== this.searchVersion || controller.signal.aborted) return;
        this.results.replaceChildren(content);
        this.status.textContent = content.querySelector('[data-result-count]')?.textContent || 'Søgeresultater klar';
      } catch (error) {
        if (error.name === 'AbortError' || version !== this.searchVersion) return;
        this.status.textContent = 'Søgeforslag er midlertidigt utilgængelige. Tryk Enter eller Søg for at se resultater.';
        const fallback = document.createElement('a'); fallback.className = 'nh-search__all';
        const target = new URL(this.input.form.action, location.origin); target.search = new URLSearchParams({ q: query, type: 'product' });
        fallback.href = target.href; fallback.textContent = `Søg efter “${query}”`;
        this.results.replaceChildren(fallback);
      } finally { if (version === this.searchVersion) this.panel.removeAttribute('aria-busy'); }
    }
    async refreshCart() {
      this.cartRequest?.abort(); this.cartRequest = new AbortController();
      try {
        const response = await fetch(this.dataset.cartUrl, { credentials: 'same-origin', signal: this.cartRequest.signal });
        if (!response.ok) return;
        const cart = await response.json();
        this.querySelector('[data-nh-cart-count]').textContent = cart.item_count;
        this.querySelector('[data-nh-cart]').setAttribute('aria-label', `Kurv med ${cart.item_count} varer`);
        this.querySelector('[data-nh-cart-total]').textContent = new Intl.NumberFormat(document.documentElement.lang || 'da', { style: 'currency', currency: cart.currency }).format(cart.total_price / 100);
      } catch (_) { /* Keep the server-rendered cart and working cart link on network failure. */ }
    }
  }
  customElements.define('nh-navigation', NHNavigation);
})();
